
import { User } from '@supabase/supabase-js';

/**
 * UserRole - Defines the standardized user roles in the system
 * 
 * SUPER_ADMIN - Full system access (company employees only)
 * ORG_ADMIN - Organisation-level administrators
 * REST_ADMIN - Restaurant-level administrators
 * USER - Base-level access
 * NEW_SIGN_UP - Users who have just signed up and need onboarding
 * IN_PROGRESS - Users who are in the middle of the onboarding process
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ORG_ADMIN = 'org_admin',
  REST_ADMIN = 'rest_admin',
  USER = 'user',
  NEW_SIGN_UP = 'new_sign_up',
  IN_PROGRESS = 'in_progress'
}

export interface Restaurant {
  id: string;
  name: string;
  accessLevel: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: UserRole | string; // Support both enum and string for backward compatibility
  jwt_role?: UserRole | string; // JWT role that may override the database role
  organisationId: string;
  organisationName?: string;
  restaurants: Restaurant[];
}

export interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  dataInitialized: boolean;
  profileFailure?: boolean; // New flag to indicate using fallback profile
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<any | null>;
}

// Role utility functions
export const hasAdminAccess = (role: UserRole | string): boolean => {
  return role === UserRole.SUPER_ADMIN || 
         role === UserRole.ORG_ADMIN || 
         role === UserRole.REST_ADMIN;
};

export const hasOrgAdminAccess = (role: UserRole | string): boolean => {
  return role === UserRole.SUPER_ADMIN || 
         role === UserRole.ORG_ADMIN;
};

export const hasSuperAdminAccess = (role: UserRole | string): boolean => {
  return role === UserRole.SUPER_ADMIN;
};

export const hasRestAdminAccess = (role: UserRole | string): boolean => {
  return role === UserRole.REST_ADMIN || hasOrgAdminAccess(role) || hasSuperAdminAccess(role);
};

// Demo user accounts for development environment
export const DEMO_USERS: (UserProfile & { password: string })[] = [
  {
    id: 'demo-owner-1',
    email: 'owner@restaurant.com',
    password: 'password',
    name: 'Demo Owner',
    role: UserRole.REST_ADMIN, // Updated from 'owner'
    organisationId: 'org-001',
    organisationName: 'Smashies Restaurants',
    restaurants: [
      { id: 'rest-001', name: 'Smashies Downtown', accessLevel: 'admin' },
      { id: 'rest-002', name: 'Smashies Uptown', accessLevel: 'admin' },
      { id: 'rest-003', name: 'Smashies West End', accessLevel: 'admin' }
    ]
  },
  {
    id: 'demo-manager-1',
    email: 'manager@restaurant.com',
    password: 'password',
    name: 'Demo Manager',
    role: UserRole.REST_ADMIN, // Updated from 'manager'
    organisationId: 'org-001',
    organisationName: 'Smashies Restaurants',
    restaurants: [
      { id: 'rest-001', name: 'Smashies Downtown', accessLevel: 'manager' }
    ]
  }
];
